import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
  Chip,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel
} from '@mui/material';
import {
  WhatsApp as WhatsAppIcon,
  CheckCircle as CheckIcon,
  Person as PersonIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AccessTime as TimezoneIcon,
  Language as LanguageIcon
} from '@mui/icons-material';
import { checkPhone, RULES } from '../../data/phoneRules';
import api from '../../services/api';

const RULE_EXAMPLE = Object.fromEntries(Object.entries(RULES).map(([cc, r]) => [cc, r.example]));

// Codes pays avec leurs fuseaux horaires par défaut
const COUNTRY_CODES = [
  // Afrique
  { code: '+237', country: 'Cameroun', flag: '🇨🇲', timezone: 'Africa/Douala' },
  { code: '+225', country: 'Côte d\'Ivoire', flag: '🇨🇮', timezone: 'Africa/Abidjan' },
  { code: '+221', country: 'Sénégal', flag: '🇸🇳', timezone: 'Africa/Dakar' },
  { code: '+241', country: 'Gabon', flag: '🇬🇦', timezone: 'Africa/Libreville' },
  { code: '+242', country: 'Congo', flag: '🇨🇬', timezone: 'Africa/Brazzaville' },
  { code: '+243', country: 'RD Congo', flag: '🇨🇩', timezone: 'Africa/Kinshasa' },
  { code: '+234', country: 'Nigeria', flag: '🇳🇬', timezone: 'Africa/Lagos' },
  { code: '+27', country: 'Afrique du Sud', flag: '🇿🇦', timezone: 'Africa/Johannesburg' },
  { code: '+212', country: 'Maroc', flag: '🇲🇦', timezone: 'Africa/Casablanca' },
  { code: '+216', country: 'Tunisie', flag: '🇹🇳', timezone: 'Africa/Tunis' },
  { code: '+213', country: 'Algérie', flag: '🇩🇿', timezone: 'Africa/Algiers' },
  // Europe
  { code: '+33', country: 'France', flag: '🇫🇷', timezone: 'Europe/Paris' },
  { code: '+32', country: 'Belgique', flag: '🇧🇪', timezone: 'Europe/Brussels' },
  { code: '+41', country: 'Suisse', flag: '🇨🇭', timezone: 'Europe/Zurich' },
  { code: '+44', country: 'Royaume-Uni', flag: '🇬🇧', timezone: 'Europe/London' },
  { code: '+49', country: 'Allemagne', flag: '🇩🇪', timezone: 'Europe/Berlin' },
  { code: '+39', country: 'Italie', flag: '🇮🇹', timezone: 'Europe/Rome' },
  { code: '+34', country: 'Espagne', flag: '🇪🇸', timezone: 'Europe/Madrid' },
  // Amérique du Nord
  { code: '+1', country: 'États-Unis / Canada', flag: '🇺🇸', timezone: 'America/New_York' },
  // Caraïbes
  { code: '+509', country: 'Haïti', flag: '🇭🇹', timezone: 'America/Port-au-Prince' },
  { code: '+596', country: 'Martinique', flag: '🇲🇶', timezone: 'America/Martinique' },
  { code: '+590', country: 'Guadeloupe', flag: '🇬🇵', timezone: 'America/Guadeloupe' },
  // Autres
  { code: '+971', country: 'Émirats Arabes Unis', flag: '🇦🇪', timezone: 'Asia/Dubai' },
  { code: '+966', country: 'Arabie Saoudite', flag: '🇸🇦', timezone: 'Asia/Riyadh' }
];

// Fuseaux horaires disponibles pour modification manuelle
const TIMEZONES = [
  { value: 'Africa/Douala', label: 'Cameroun (GMT+1)' },
  { value: 'Africa/Abidjan', label: 'Côte d\'Ivoire (GMT+0)' },
  { value: 'Africa/Dakar', label: 'Sénégal (GMT+0)' },
  { value: 'Africa/Lagos', label: 'Nigeria (GMT+1)' },
  { value: 'Africa/Kinshasa', label: 'RD Congo - Ouest (GMT+1)' },
  { value: 'Africa/Lubumbashi', label: 'RD Congo - Est (GMT+2)' },
  { value: 'Africa/Johannesburg', label: 'Afrique du Sud (GMT+2)' },
  { value: 'Africa/Casablanca', label: 'Maroc (GMT+0/+1)' },
  { value: 'Europe/Paris', label: 'France (GMT+1/+2)' },
  { value: 'Europe/Brussels', label: 'Belgique (GMT+1/+2)' },
  { value: 'Europe/Zurich', label: 'Suisse (GMT+1/+2)' },
  { value: 'Europe/London', label: 'Royaume-Uni (GMT+0/+1)' },
  { value: 'America/New_York', label: 'États-Unis - Est (GMT-5/-4)' },
  { value: 'America/Chicago', label: 'États-Unis - Centre (GMT-6/-5)' },
  { value: 'America/Denver', label: 'États-Unis - Montagne (GMT-7/-6)' },
  { value: 'America/Los_Angeles', label: 'États-Unis - Pacifique (GMT-8/-7)' },
  { value: 'America/Montreal', label: 'Canada - Est (GMT-5/-4)' },
  { value: 'America/Vancouver', label: 'Canada - Pacifique (GMT-8/-7)' },
  { value: 'America/Port-au-Prince', label: 'Haïti (GMT-5/-4)' },
  { value: 'America/Martinique', label: 'Martinique (GMT-4)' },
  { value: 'Asia/Dubai', label: 'Dubaï (GMT+4)' }
];

// Langues de traduction disponibles
// Note: Les IDs correspondent à la table languages en base de données:
// 1 = Duala (langue source, pas utilisée ici)
// 2 = Français
// 3 = English
const TRANSLATION_LANGUAGES = [
  { id: 2, code: 'fr', label: 'Français', flag: '🇫🇷' },
  { id: 3, code: 'en', label: 'English', flag: '🇬🇧' }
];

const Subscribe = () => {
  const [formData, setFormData] = useState({
    phone_number: '',
    country_code: '+237',
    first_name: '',
    last_name: '',
    timezone: 'Africa/Douala',
    preferred_language_id: 2 // Français par défaut (id=2 dans la table languages)
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [showTimezoneSelect, setShowTimezoneSelect] = useState(false);

  // Mettre à jour le fuseau horaire quand le code pays change
  useEffect(() => {
    const country = COUNTRY_CODES.find(c => c.code === formData.country_code);
    if (country) {
      setFormData(prev => ({ ...prev, timezone: country.timezone }));
      setShowTimezoneSelect(false); // Fermer le sélecteur si ouvert
    }
  }, [formData.country_code]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const getTimezoneLabel = (timezoneValue) => {
    const tz = TIMEZONES.find(t => t.value === timezoneValue);
    return tz ? tz.label : timezoneValue;
  };

  const getCurrentCountry = () => {
    return COUNTRY_CODES.find(c => c.code === formData.country_code);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Prénom, nom et numéro obligatoires
    const firstName = formData.first_name.trim();
    const lastName = formData.last_name.trim();
    if (!firstName) { setError('Veuillez entrer votre prénom'); return; }
    if (firstName.length < 3) { setError('Le prénom doit contenir au moins 3 caractères'); return; }
    if (!lastName) { setError('Veuillez entrer votre nom'); return; }
    if (lastName.length < 3) { setError('Le nom doit contenir au moins 3 caractères'); return; }
    // Numéro vérifié selon le pays choisi (mêmes règles que le serveur)
    const phoneCheck = checkPhone(formData.country_code, formData.phone_number);
    if (!phoneCheck.ok) { setError(phoneCheck.message); return; }
    const phone = phoneCheck.digits;

    try {
      setLoading(true);
      await api.post('/subscribers', { ...formData, first_name: firstName, last_name: lastName, phone_number: phone });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <CheckIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
        <Typography variant="h5" gutterBottom fontWeight="bold" color="success.main">
          Inscription réussie !
        </Typography>
        <Typography color="textSecondary" sx={{ mb: 3 }}>
          Votre demande d'inscription a été enregistrée. Un administrateur validera votre inscription prochainement.
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Vous recevrez votre premier éphéméride dès que votre inscription sera validée.
        </Typography>
        <Button
          variant="outlined"
          sx={{ mt: 3 }}
          onClick={() => {
            setSuccess(false);
            setFormData({
              phone_number: '',
              country_code: '+237',
              first_name: '',
              last_name: '',
              timezone: 'Africa/Douala',
              preferred_language_id: 2
            });
          }}
        >
          Nouvelle inscription
        </Button>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: { xs: 2, sm: 4 } }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <WhatsAppIcon sx={{ fontSize: 60, color: '#25D366', mb: 2 }} />
          <Typography variant="h4" gutterBottom fontWeight="bold">
            Éphéméride Duala
          </Typography>
          <Typography color="textSecondary">
            Recevez chaque jour un proverbe en langue Duala sur WhatsApp
          </Typography>
        </Box>

        <Divider sx={{ mb: 4 }} />

        {/* Avantages */}
        <Typography variant="h6" component="h2" gutterBottom>
          Ce que vous recevrez :
        </Typography>
        <List dense>
          <ListItem>
            <ListItemIcon>
              <CheckIcon color="success" />
            </ListItemIcon>
            <ListItemText 
              primary="Un proverbe Duala chaque matin" 
              secondary="Avec sa traduction dans votre langue"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckIcon color="success" />
            </ListItemIcon>
            <ListItemText 
              primary="Une image éphéméride" 
              secondary="Format calendrier avec date en Duala"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckIcon color="success" />
            </ListItemIcon>
            <ListItemText 
              primary="Gratuit et sans engagement" 
              secondary="Désabonnement possible à tout moment"
            />
          </ListItem>
        </List>

        <Divider sx={{ my: 4 }} />

        {/* Formulaire */}
        <Typography variant="h6" component="h2" gutterBottom>
          Inscrivez-vous
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          {/* Numéro de téléphone */}
          <TextField
            fullWidth
            label="Numéro WhatsApp"
            name="phone_number"
            value={formData.phone_number}
            onChange={handleChange}
            required
            type="tel"
            inputProps={{ inputMode: 'numeric', autoComplete: 'tel-national' }}
            placeholder={RULE_EXAMPLE[formData.country_code] || ''}
            helperText={`Numéro sans le code pays${RULE_EXAMPLE[formData.country_code] ? ` — exemple : ${RULE_EXAMPLE[formData.country_code]}` : ''}`}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FormControl variant="standard" size="small" sx={{ minWidth: 120 }}>
                    <Select
                      name="country_code"
                      inputProps={{ 'aria-label': 'Indicatif du pays' }}
                      SelectDisplayProps={{ 'aria-label': 'Indicatif du pays' }}
                      value={formData.country_code}
                      onChange={handleChange}
                      disableUnderline
                      renderValue={(value) => {
                        const country = COUNTRY_CODES.find(c => c.code === value);
                        return country ? `${country.flag} ${value}` : value;
                      }}
                    >
                      {COUNTRY_CODES.map((country) => (
                        <MenuItem key={country.code} value={country.code}>
                          {country.flag} {country.code} - {country.country}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </InputAdornment>
              )
            }}
            sx={{ mb: 2 }}
          />

          {/* Fuseau horaire (automatique avec option de modification) */}
          <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TimezoneIcon color="action" />
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Fuseau horaire
                  </Typography>
                  <Typography variant="body1">
                    {getTimezoneLabel(formData.timezone)}
                  </Typography>
                </Box>
              </Box>
              <Button
                size="small"
                onClick={() => setShowTimezoneSelect(!showTimezoneSelect)}
                endIcon={showTimezoneSelect ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              >
                Modifier
              </Button>
            </Box>
            
            <Collapse in={showTimezoneSelect}>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Choisir un fuseau horaire</InputLabel>
                <Select
                  name="timezone"
                  value={formData.timezone}
                  onChange={handleChange}
                  label="Choisir un fuseau horaire"
                >
                  {TIMEZONES.map((tz) => (
                    <MenuItem key={tz.value} value={tz.value}>
                      {tz.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Collapse>
            
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
              L'éphéméride sera envoyée à 6h00 de votre heure locale
            </Typography>
          </Box>

          {/* Prénom / Nom */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              label="Prénom"
              name="first_name"
              inputProps={{ minLength: 3, maxLength: 100 }}
              value={formData.first_name}
              onChange={handleChange}
              required
              autoComplete="given-name"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon color="action" />
                  </InputAdornment>
                )
              }}
            />
            <TextField
              fullWidth
              label="Nom"
              name="last_name"
              inputProps={{ minLength: 3, maxLength: 100 }}
              value={formData.last_name}
              onChange={handleChange}
              required
              autoComplete="family-name"
            />
          </Box>

          {/* Langue de traduction */}
          <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
            <FormLabel component="legend" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <LanguageIcon fontSize="small" />
              Langue de traduction
            </FormLabel>
            <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: 'block' }}>
              Le proverbe original est en Duala. Choisissez la langue de la traduction.
            </Typography>
            <RadioGroup
              name="preferred_language_id"
              value={formData.preferred_language_id}
              onChange={(e) => setFormData(prev => ({ ...prev, preferred_language_id: parseInt(e.target.value) }))}
              row
            >
              {TRANSLATION_LANGUAGES.map((lang) => (
                <FormControlLabel
                  key={lang.id}
                  value={lang.id}
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <span>{lang.flag}</span>
                      <span>{lang.label}</span>
                    </Box>
                  }
                  sx={{
                    flex: 1,
                    m: 0,
                    p: 1.5,
                    border: '1px solid',
                    borderColor: formData.preferred_language_id === lang.id ? 'primary.main' : 'grey.300',
                    borderRadius: 2,
                    bgcolor: formData.preferred_language_id === lang.id ? 'primary.50' : 'transparent',
                    mr: 1
                  }}
                />
              ))}
            </RadioGroup>
          </FormControl>

          {/* Bouton d'inscription */}
          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <WhatsAppIcon />}
            sx={{ 
              bgcolor: '#25D366', 
              '&:hover': { bgcolor: '#128C7E' },
              py: 1.5
            }}
          >
            {loading ? 'Inscription en cours...' : 'S\'inscrire'}
          </Button>
        </Box>

        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
          En vous inscrivant, vous acceptez de recevoir des messages WhatsApp quotidiens.
          Votre numéro ne sera pas partagé.
        </Typography>
      </Paper>
  );
};

export default Subscribe;